/**
 * refinementCoordinator — FINAL system prompt layer (R3 simplified).
 *
 * Uses 4 sub-engines: signalPriority, semanticDedup, globalCoherence, simplicityBalancer.
 * Produces a plain instruction block ≤150 chars — no header, no analytical framing.
 *
 * Signal-gated: only active when dedup, coherence, or simplicity requires correction.
 */

import { resolveSignalPriority }    from "./signalPriorityResolver";
import { assessCognitiveLoad }       from "./cognitiveLoadReducer";
import { runSemanticDeduplication }  from "./semanticDeduplicationEngine";
import { assessGlobalCoherence }     from "./globalPastoralCoherence";
import { balanceResponseSimplicity } from "./responseSimplicityBalancer";

const MAX_REFINEMENT_CHARS = 150;

function isRefinementActive({ signalCount, loadLevel, dedupCount, coherenceState, simplicityLevel }) {
  // R7: raised threshold — prefer silence over correction when signals are borderline
  return (
    signalCount    >= 5           ||
    loadLevel      === "high"     ||
    dedupCount     >= 2           ||  // raised from 1 — single dedup hit no longer fires
    coherenceState !== "stable"   ||
    simplicityLevel !== "normal"
  );
}

export function buildRefinementContext({
  // Signal inputs
  riskLevel,
  emotion,
  complexity,
  silentTension,
  exhaustionState,
  isolationState,
  rhythmState,
  regressionState,
  conflictType,
  loadState,
  boundaryState,
  escalation,
  pace,
  // Layer state
  formationPresence,
  guidanceType,
  timing,
  depth,
  capacity,
  readiness,
  convMode,
  familyDynamic,
  // Active block count
  activeBlockCount,
  // Raw block texts for dedup/compression
  blockTexts,
}) {
  const signalResult     = resolveSignalPriority({ riskLevel, emotion, complexity, silentTension, exhaustionState, isolationState, rhythmState, regressionState, conflictType, loadState, boundaryState, escalation, pace });
  const loadResult       = assessCognitiveLoad({ signalCount: signalResult.signalCount, activeBlockCount: activeBlockCount ?? 0, emotion, complexity, conflictType, familyDynamic, rhythmState, exhaustionState });
  const dedupResult      = runSemanticDeduplication(blockTexts ?? []);
  const coherenceResult  = assessGlobalCoherence({ presence: formationPresence, emotion, depth, convMode, pace, exhaustionState, capacity, activeBlockCount: activeBlockCount ?? 0 });
  const simplicityResult = balanceResponseSimplicity({ emotion, exhaustionState, silentTension, capacity, readiness });

  const isActive = isRefinementActive({
    signalCount:     signalResult.signalCount,
    loadLevel:       loadResult.loadLevel,
    dedupCount:      dedupResult.dedupCount,
    coherenceState:  coherenceResult.coherenceState,
    simplicityLevel: simplicityResult.simplicityLevel,
  });

  const base = {
    signalCount:     signalResult.signalCount,
    loadLevel:       loadResult.loadLevel,
    dedupCount:      dedupResult.dedupCount,
    coherenceState:  coherenceResult.coherenceState,
    simplicityLevel: simplicityResult.simplicityLevel,
    isActive,
  };

  if (!isActive) {
    return { ...base, refinementContext: null };
  }

  // Collect instructions — priority-ordered, no duplicates
  const seen         = new Set();
  const instructions = [];

  function addInstruction(txt) {
    if (!txt) return;
    const key = txt.trim().toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    instructions.push(txt.trim());
  }

  // Priority 1 — Coherence corrections
  addInstruction(coherenceResult.instruction);

  // Priority 2 — Simplicity directives
  addInstruction(simplicityResult.instruction);

  // Priority 3 — Cognitive load
  addInstruction(loadResult.instruction);

  // Priority 4 — Deduplicated canonicals
  for (const canonical of dedupResult.canonicals) addInstruction(canonical);

  // Build block — max 2 instructions, ≤150 chars, no header
  const body    = instructions.slice(0, 2).join(" ");
  const trimmed = body.length > MAX_REFINEMENT_CHARS
    ? body.slice(0, MAX_REFINEMENT_CHARS - 1) + "…"
    : body;

  return { ...base, refinementContext: trimmed || null };
}
